import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Checkbox,
  Container,
  InputBase,
  Typography,
  CircularProgress,
  Stack,
  IconButton,
  InputAdornment,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'

import { useAuth } from '@/hooks/useAuth'
import logo from '@/assets/img/spk_icon_trans.png'
import GoogleLoginButton from '@/components/GoogleLoginButton'
import CleverButton from '@/components/CleverLoginButton'
import useKeyPress from '@/utils/useKeyPress'
import AuthLayout from '@/components/AuthLayout'
import PasswordResetModal from '@/components/PasswordResetModal'
import { scrollBarStyles } from '@/assets/css/scrollbar'
import { ReactComponent as CheckBoxIcon } from '@/assets/icons/checkbox-icon.svg'
import { ReactComponent as CheckBoxCheckedIcon } from '@/assets/icons/checkbox-checked-filled-icon.svg'

function CreateAccount({ isLogin = false }) {
  const navigate = useNavigate()
  const { createAccountWithEmail, loginWithEmail } = useAuth()
  const [passwordResetOpen, setPasswordResetOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [terms, setTerms] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const queryParams = new URLSearchParams(window.location.search)
  const redirect = queryParams.get('redirect') || '/' // Default to the homepage if no redirect is specified

  const isValidEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    return emailRegex.test(email.trim())
  }

  const isFormValid = () => {
    if (isLogin) {
      return email.trim() !== '' && password.trim() !== '' && isValidEmail(email)
    } else {
      return email.trim() !== '' && password.trim() !== '' && name.trim() !== '' && terms && isValidEmail(email)
    }
  }

  const handleEmailCreateAccount = async e => {
    if (!terms) {
      alert('Please agree to the terms and conditions')

      return
    }
    e.preventDefault()
    setLoading(true)
    const result = await createAccountWithEmail(email, password, name)

    if (result.error) {
      alert(result.message)
      setLoading(false)

      return
    }
    setLoading(false)
    navigate(`/onboarding${redirect !== '/' ? `?redirect=${encodeURIComponent(redirect)}` : ''}`)
  }
  const handleEmailLogin = async e => {
    e.preventDefault()
    setLoading(true)
    const { profile, error, message } = await loginWithEmail(email, password)

    if (error) {
      alert(message)
      setLoading(false)

      return
    }
    setLoading(false)
    if (!profile?.roles) {
      navigate(`/onboarding${redirect !== '/' ? `?redirect=${encodeURIComponent(redirect)}` : ''}`)
    } else {
      navigate(redirect)
    }
  }

  const handleSetEmail = e => {
    e.preventDefault()
    setEmail(e.target.value)
  }
  const handleSetPassword = e => {
    e.preventDefault()
    setPassword(e.target.value)
  }
  const handleSetName = e => {
    e.preventDefault()
    setName(e.target.value)
  }

  useKeyPress(['Enter'], e => {
    if (isLogin) {
      handleEmailLogin(e)

      return
    }
    if (!terms) {
      alert('Please agree to the terms and conditions')

      return
    }
    if (email && password && name) {
      handleEmailCreateAccount(e)
    } else {
      alert('Please fill out all fields')

      return
    }
  })

  return (
    <Container
      sx={{
        minHeight: '100vh',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: 'white',
        overflow: 'auto',
        ...scrollBarStyles,
      }}
    >
      <Stack gap="32px" maxWidth={360} width="100%">
        {/* Header */}
        <Stack alignItems="center" gap="24px">
          <img
            alt="logo"
            src={logo}
            style={{
              maxWidth: '100%',
              height: '48.16px',
            }}
          />
          <Stack alignItems="center" gap="12px">
            <Typography fontSize={30} fontWeight={600} lineHeight="38px" textAlign="center">
              {isLogin ? 'Welcome back' : 'Create Free Account'}
            </Typography>
            <Typography fontWeight={400} lineHeight="20px" textAlign="center">
              {isLogin
                ? `Login to get started with Speakable.`
                : `Sign up and unlock new possibilities with Speakable!`}
            </Typography>
          </Stack>
        </Stack>
        {/* Form */}
        <Stack gap="24px">
          <Stack gap="20px">
            {!isLogin && (
              <CustomTextField placeholder="Full Name" title="Full Name*" value={name} onChange={handleSetName} />
            )}
            <CustomTextField placeholder="Email" title="Email*" value={email} onChange={handleSetEmail} />
            <CustomTextField
              fullWidth
              placeholder="password"
              showPassword={showPassword}
              title="Password*"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={handleSetPassword}
              onTogglePassword={() => setShowPassword(!showPassword)}
            />
            {!isLogin && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox
                  checked={terms}
                  checkedIcon={<CheckBoxCheckedIcon style={{ height: 20, width: 20 }} />}
                  color="secondary"
                  icon={<CheckBoxIcon style={{ height: 20, width: 20 }} />}
                  sx={{ p: 0 }}
                  value="remember"
                  onChange={() => setTerms(!terms)}
                />

                <Typography color="secondary.main" fontSize={12} variant="caption">
                  By creating an account, you agree to our{' '}
                  <a
                    href="https://speakable.io/terms"
                    rel="noopener"
                    style={{
                      color: '#A12F7E',
                    }}
                    target="_blank"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://speakable.io/terms"
                    rel="noopener"
                    style={{
                      color: '#A12F7E',
                    }}
                    target="_blank"
                  >
                    Privacy Policy
                  </a>
                </Typography>
              </Box>
            )}
          </Stack>
          <Stack gap="16px">
            <Button
              disableElevation
              fullWidth
              color="primary"
              disabled={loading || !isFormValid()}
              size="large"
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{ height: 44, fontSize: 16, fontWeight: 600, lineHeight: '24px' }}
              variant="contained"
              onClick={isLogin ? handleEmailLogin : handleEmailCreateAccount}
            >
              {isLogin ? 'Login' : 'Create Account'}
            </Button>
            <GoogleLoginButton
              redirect={redirect}
              size="large"
              sx={{
                maxWidth: 400,
                textTransform: 'none',
                borderRadius: '8px',
                border: '1px solid #D0D5DD',
                background: '#FFF',
                boxShadow: '0px 1px 16px 0px rgba(16, 24, 40, 0.10)',
                '&:hover': {
                  boxShadow: 'none',
                  border: '1px solid #D0D5DD',
                },
              }}
            />
            <CleverButton
              size="large"
              sx={{
                maxWidth: 400,
                borderRadius: '8px',
                border: '1px solid #D0D5DD',
                background: '#FFF',
                boxShadow: '0px 1px 16px 0px rgba(16, 24, 40, 0.10)',
                '&:hover': {
                  boxShadow: 'none',
                  border: '1px solid #D0D5DD',
                },
              }}
            />
          </Stack>
        </Stack>
        {/* Footer */}
        <Stack alignItems="center" gap="16px">
          {isLogin ? (
            <>
              <Typography color="#475467" lineHeight="20px" variant="caption">
                Don&apos;t have an account?{' '}
                <Link
                  style={{
                    color: '#A12F7E',
                    fontWeight: 600,
                  }}
                  to={`/auth/create-account${window.location.search}`}
                >
                  Create
                </Link>
              </Typography>
              <Typography
                color="#475467"
                fontSize={12}
                fontWeight={400}
                sx={{
                  '&:hover': {
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    color: '#A12F7E',
                  },
                }}
                variant="caption"
                onClick={() => setPasswordResetOpen(true)}
              >
                I forgot my password.{' '}
              </Typography>
            </>
          ) : (
            <Typography color="#475467" lineHeight="20px" variant="caption">
              Already have an account?{' '}
              <Link
                style={{
                  color: '#A12F7E',
                  fontWeight: 600,
                }}
                to={`/auth/login${window.location.search}`}
              >
                Login
              </Link>
            </Typography>
          )}
        </Stack>
      </Stack>
      <PasswordResetModal open={passwordResetOpen} onClose={() => setPasswordResetOpen(false)} />
    </Container>
  )
}

function CreateAccountPage({ isLogin = false }) {
  return (
    <AuthLayout>
      <CreateAccount isLogin={isLogin} />
    </AuthLayout>
  )
}

export default CreateAccountPage

function CustomTextField({ title, value, onChange, showPassword, onTogglePassword, ...props }) {
  return (
    <Stack gap="6px">
      {title ? (
        <Typography color="#344054" fontSize={14} fontWeight={500} lineHeight="20px">
          {title}
        </Typography>
      ) : null}
      <InputBase
        fullWidth
        endAdornment={
          props.type === 'password' || props.type === 'text' ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                edge="end"
                size="small"
                sx={{ color: '#667085' }}
                onClick={onTogglePassword}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ) : null
        }
        sx={{
          p: '10px 14px',
          fontSize: 16,
          fontWeight: 400,
          lineHeight: '20px',
          '&.MuiInputBase-root': {
            borderRadius: '8px !important',
            border: '1px solid #D0D5DD !important',
            background: '#FFF',
            boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
            height: '44px',
          },
        }}
        value={value}
        onChange={onChange}
        {...props}
      />
    </Stack>
  )
}
